/**
 * Feed Slide Generators for PowerPoint Export
 * Shared utilities for rendering feed items in PPT format
 * Used by both Feed and Profile exports for consistency
 */

import {
  AVATAR_SIZE,
  COLOR_FEED_CARD_BG,
  COLOR_FEED_CARD_BORDER,
  COLOR_FEED_META,
  COLOR_FEED_PROFILE_NAME,
  COLOR_FEED_SNIPPET,
  COLOR_FEED_TITLE,
  COLOR_FEED_TYPE,
  COLOR_FEED_VIEW_BG,
  COLOR_FEED_VIEW_TEXT,
} from 'common/Card/ppt-constants';

import { FEED_LAYOUT } from 'common/utils/ppt/chartLayoutConstants';
import { formatNotesFromFilters } from './notesUtils';
import { sanitizeUrlForPPT } from './urlSanitizer';

/**
 * Sanitize text content for use in PowerPoint
 * Removes or replaces characters that can cause issues in PPT
 * @param {string} text - Text to sanitize
 * @returns {string} - Sanitized text
 */
const sanitizeTextForPPT = text => {
  if (!text) return '';

  return (
    text
      .replace(/\u00A0/g, ' ') // Replace non-breaking spaces with regular spaces
      .replace(/[\u2018\u2019]/g, "'") // Replace smart quotes with regular quotes
      .replace(/[\u201C\u201D]/g, '"') // Replace smart double quotes
      .replace(/\u2013/g, '-') // Replace en dash with hyphen
      .replace(/\u2014/g, '--') // Replace em dash with double hyphen
      .replace(/\u2026/g, '...') // Replace ellipsis character with three dots
      .replace(/[\u007F]/g, '') // Remove DEL character
      // eslint-disable-next-line no-control-regex
      .replace(/[\u0001-\u0008\u000B-\u000C\u000E-\u001F]/g, '') // Remove other control chars except NULL
      .replace(/\t+/g, ' ') // Replace tabs with single space
      .replace(/\n\s*\n/g, '\n') // Remove empty lines with whitespace
      .replace(/^\s*\n/gm, '') // Remove lines that are only whitespace
      .trim() // Trim leading/trailing whitespace
  );
};

/**
 * Generate feed slides with consistent 2x2 grid layout
 * @param {Object} pptx - PptxGenJS instance
 * @param {Array} feedData - Array of feed items
 * @param {Map} avatarMap - Map of avatar URLs to base64 data
 * @param {Function} addSlideHeader - Function to add slide header
 * @param {Function} addSlideFooter - Function to add slide footer
 * @param {Function} pptFormatDate - Function to format dates for PPT
 * @param {string} linkIconBase64 - Base64 encoded link icon image
 * @param {string} defaultAvatarBase64 - Base64 encoded default avatar image for fallback
 * @returns {void}
 */
export const generateFeedSlides = (
  pptx,
  feedData,
  avatarMap,
  addSlideHeader,
  addSlideFooter,
  pptFormatDate,
  linkIconBase64,
  defaultAvatarBase64 = null,
  feedFilters = null,
) => {
  // Validate inputs - fail fast
  if (!pptx) {
    throw new Error('[generateFeedSlides] PPTX object is required');
  }

  if (!feedData || !Array.isArray(feedData)) {
    throw new Error(
      `[generateFeedSlides] Feed data must be an array, received: ${typeof feedData}`,
    );
  }

  if (feedData.length === 0) {
    return; // Empty feed data, skip slide generation
  }

  if (!addSlideHeader || typeof addSlideHeader !== 'function') {
    throw new Error('[generateFeedSlides] addSlideHeader must be a function');
  }

  if (!addSlideFooter || typeof addSlideFooter !== 'function') {
    throw new Error('[generateFeedSlides] addSlideFooter must be a function');
  }
  // Image deduplication: Track unique base64 strings we've already added
  const imageCache = new Map(); // base64 -> pptx image reference

  // Use centralized layout constants for consistency with chart exports
  const feedItemsPerSlide = FEED_LAYOUT.ITEMS_PER_SLIDE;
  const boxW = FEED_LAYOUT.CARD_WIDTH;
  const boxH = FEED_LAYOUT.CARD_HEIGHT;
  const hSpacing = FEED_LAYOUT.H_SPACING;
  const vSpacing = FEED_LAYOUT.V_SPACING;
  const startX = FEED_LAYOUT.START_X;
  const startY = FEED_LAYOUT.START_Y;

  // Process feed items in chunks of 4
  for (let i = 0; i < feedData.length; i += feedItemsPerSlide) {
    const chunk = feedData.slice(i, i + feedItemsPerSlide);
    const slideNum = Math.floor(i / feedItemsPerSlide) + 1;

    let slide;
    try {
      slide = pptx.addSlide({ masterName: 'CONTENT_SLIDE' });
    } catch (error) {
      throw new Error(`Failed to create feed slide ${slideNum}: ${error.message}`);
    }

    try {
      addSlideHeader(slide, 'Feed');
    } catch (error) {
      throw new Error(`Failed to add header to feed slide ${slideNum}: ${error.message}`);
    }

    try {
      addSlideFooter(slide);
    } catch (error) {
      throw new Error(`Failed to add footer to feed slide ${slideNum}: ${error.message}`);
    }

    // Add filter notes to the feed slide
    if (feedFilters) {
      try {
        const noteText = formatNotesFromFilters(
          'Feed',
          feedFilters.startDate,
          feedFilters.endDate,
          feedFilters.filters,
          feedFilters.resolveFilterValue, // Pass resolveFilterValue function
        );
        slide.addNotes(noteText);
      } catch (error) {
        // Continue with export even if notes fail
      }
    }

    chunk.forEach((item, j) => {
      const itemIndex = i + j; // Calculate actual item index in feedData
      const positionIndex = j; // Position on the current slide (0-3)
      const row = Math.floor(j / 2);
      const col = j % 2;
      const x = startX + col * (boxW + hSpacing);
      const y = startY + row * (boxH + vSpacing);

      // Card with rounded corners and white background
      try {
        slide.addShape(pptx.ShapeType.roundRect, {
          x,
          y,
          w: boxW,
          h: boxH,
          fill: { color: COLOR_FEED_CARD_BG }, // White background for card
          line: {
            color: COLOR_FEED_CARD_BORDER,
            width: 0.75,
            transparency: 47, // 47% transparency
          },
          rectRadius: 0.1, // Rounded corners (reduced from 0.15)
        });
      } catch (error) {
        throw new Error(
          `Failed to add box border for feed item ${itemIndex} on slide ${slideNum}: ${error.message}`,
        );
      }

      // Avatar with proper fallback
      const avatarUrl = item.profileUrl;
      let avatarBase64 = null;

      // Try to get the specific avatar, or use default
      if (avatarUrl) {
        avatarBase64 = avatarMap.get(avatarUrl);
      }

      // If no avatar found (either no URL or not in map), use default
      if (!avatarBase64) {
        avatarBase64 = defaultAvatarBase64;
      }

      if (avatarBase64) {
        try {
          // Ensure the base64 string is properly formatted
          let imageData = avatarBase64;
          if (!imageData.startsWith('data:')) {
            // If it doesn't have the data URI prefix, add it
            imageData = `data:image/png;base64,${imageData}`;
          }

          // Check if we've already added this exact image
          if (!imageCache.has(imageData)) {
            // First time seeing this image, add it and cache
            const imageNumber = imageCache.size + 1;
            imageCache.set(imageData, imageNumber);
          }

          // Add image with explicit error handling
          const imageOptions = {
            data: imageData,
            x: x + 0.2,
            y: y + 0.2,
            w: AVATAR_SIZE,
            h: AVATAR_SIZE,
            rounding: true, // Circular avatar to match profile cards
          };

          // Add image with error handling
          try {
            slide.addImage(imageOptions);
          } catch (imageError) {
            throw new Error(
              `Failed to add avatar image for ${item.profileName} on slide ${slideNum}, position ${
                positionIndex + 1
              }/4. Image data length: ${imageData?.length || 0}, Error: ${imageError.message}`,
            );
          }
        } catch (error) {
          // Re-throw with context for debugging
          throw new Error(
            `Failed to process avatar for ${item.profileName || 'Unknown'} on slide ${slideNum}. ` +
              `Original error: ${error.message}`,
          );
        }
      } else {
        // Fallback placeholder circle when no avatar is available
        try {
          slide.addShape('ellipse', {
            x: x + 0.2,
            y: y + 0.2,
            w: AVATAR_SIZE,
            h: AVATAR_SIZE,
            fill: { color: 'E0E0E0' }, // Light gray placeholder
          });
        } catch (error) {
          // Continue without placeholder if it fails
        }
      }

      // Profile name (no hyperlink) - with error handling
      const profileName = sanitizeTextForPPT(item.profileName || 'Unknown Profile');
      try {
        slide.addText(profileName, {
          x: x + 0.75,
          y: y + 0.28, // Adjusted to better align with avatar center
          fontSize: 11, // Slightly larger
          bold: true,
          color: COLOR_FEED_PROFILE_NAME, // Dark blue-gray for profile name
          wrap: true,
        });
      } catch (error) {
        throw new Error(
          `Failed to add profile name "${profileName}" on slide ${slideNum}, position ${
            positionIndex + 1
          }. Error: ${error.message}`,
        );
      }

      // Type - with error handling
      const itemType = item.type || 'Post';
      try {
        slide.addText(itemType, {
          x: x + 0.75,
          y: y + 0.45,
          fontSize: 9,
          bold: false, // Not bold per mockup
          color: COLOR_FEED_TYPE, // Light gray for type
        });
      } catch (error) {
        throw new Error(
          `Failed to add type "${itemType}" on slide ${slideNum}, position ${
            positionIndex + 1
          }. Error: ${error.message}`,
        );
      }

      // Content text - use dynamic sizing based on content length
      try {
        // Sanitize title and snippet to remove problematic characters
        // Handle null title case - if title is null/empty, use snippet as the main content
        const rawTitle = item.title || '';
        const rawSnippet = item.snippet || '';

        // If title is empty/null but snippet exists, treat snippet as the main content
        const itemTitle = rawTitle ? sanitizeTextForPPT(rawTitle) : '';
        const itemSnippet = sanitizeTextForPPT(rawSnippet);

        // Normalize for comparison - remove extra whitespace and newlines but keep ellipsis for detection
        const normalizeForComparison = text =>
          text
            .replace(/\n+/g, ' ') // Replace newlines with spaces
            .replace(/\s+/g, ' ') // Collapse multiple spaces
            .toLowerCase()
            .trim();

        const normalizedTitle = normalizeForComparison(itemTitle);
        const normalizedSnippet = normalizeForComparison(itemSnippet);

        // Simple check: if snippet is the start of the title (very common pattern)
        const snippetEndsWithEllipsis = itemSnippet.trim().endsWith('...');
        const cleanSnippet = snippetEndsWithEllipsis
          ? normalizedSnippet.substring(0, normalizedSnippet.length - 3).trim()
          : normalizedSnippet;

        // Check if snippet starts with ellipsis (common for continuation snippets)
        const snippetStartsWithEllipsis = itemSnippet.trim().startsWith('...');
        const cleanSnippetStart = snippetStartsWithEllipsis
          ? normalizedSnippet.substring(3).trim()
          : normalizedSnippet;

        // Check if snippet is duplicate of title (beginning, middle, or end)
        const snippetIsDuplicate =
          normalizedSnippet &&
          normalizedTitle &&
          (normalizedTitle === normalizedSnippet || // Exact match
            // Most common case: snippet is truncated title with ellipsis
            (snippetEndsWithEllipsis && normalizedTitle.startsWith(cleanSnippet)) ||
            // Check if snippet is the END of the title (starts with ...)
            (snippetStartsWithEllipsis && normalizedTitle.endsWith(cleanSnippetStart)) ||
            // Check if snippet is contained within the title
            (snippetStartsWithEllipsis && normalizedTitle.includes(cleanSnippetStart)) ||
            // Check if they share the same beginning (first 100-300 chars)
            (normalizedTitle.length > 100 &&
              normalizedSnippet.length > 100 &&
              normalizedTitle.substring(0, Math.min(300, cleanSnippet.length)) ===
                cleanSnippet.substring(0, Math.min(300, cleanSnippet.length))) ||
            // Check if one starts with significant portion of the other
            (normalizedTitle.length > 50 &&
              normalizedSnippet.startsWith(normalizedTitle.substring(0, 50))) ||
            (normalizedSnippet.length > 50 &&
              normalizedTitle.startsWith(normalizedSnippet.substring(0, 50))));

        // Check if snippet is effectively empty (just whitespace) or duplicate
        const hasMeaningfulSnippet =
          itemSnippet && itemSnippet.trim().length > 0 && !snippetIsDuplicate;

        // If snippet is empty/whitespace, treat as title-only
        const hasBothTitleAndSnippet = itemTitle && hasMeaningfulSnippet;

        // Calculate content metrics for better sizing
        // Use the cleaned versions for more accurate estimates
        const cleanTitle = itemTitle.replace(/\n\n+/g, '\n').trim();
        const cleanedSnippetText = hasMeaningfulSnippet
          ? itemSnippet.replace(/\n\n+/g, '\n').trim()
          : '';

        const totalContentLength =
          cleanTitle.length + (hasMeaningfulSnippet ? cleanedSnippetText.length : 0);
        // Estimate lines based on average characters per line in the card width
        const estimatedTitleLines = Math.ceil(cleanTitle.length / 65); // ~65 chars per line at card width
        const estimatedSnippetLines = hasMeaningfulSnippet
          ? Math.ceil(cleanedSnippetText.length / 70)
          : 0; // slightly more for smaller text
        const estimatedTotalLines = estimatedTitleLines + estimatedSnippetLines;

        // Base font sizes - will be adjusted based on content
        let titleFontSize = 11; // Start with larger default
        let snippetFontSize = 10; // Start with larger default

        // More granular sizing based on actual content
        if (totalContentLength <= 200) {
          // Very short content - use maximum sizes
          titleFontSize = 12;
          snippetFontSize = 11;
        } else if (totalContentLength <= 400) {
          // Short content - use large sizes
          titleFontSize = 11;
          snippetFontSize = 10.5;
        } else if (totalContentLength <= 550) {
          // Medium content - keep reasonably large
          titleFontSize = 10.5;
          snippetFontSize = 10; // Keep snippet readable
        } else if (totalContentLength <= 700) {
          // Medium-long content - still readable
          titleFontSize = 10;
          snippetFontSize = 9.5; // Keep snippet readable
        } else if (totalContentLength <= 850) {
          // Long content
          titleFontSize = 9;
          snippetFontSize = 9; // Keep same as title for balance
        } else if (totalContentLength <= 1000) {
          // Very long content
          titleFontSize = 8.5;
          snippetFontSize = 8.5; // Keep same as title
        } else {
          // Extremely long content - but keep readable minimums
          titleFontSize = 8;
          snippetFontSize = 8; // Don't go below 8 for readability
        }

        // Line-based adjustments - only for very high line counts
        if (estimatedTotalLines > 14) {
          // Extremely high line count - need smaller sizes
          titleFontSize = Math.min(titleFontSize, 7.5);
          snippetFontSize = Math.min(snippetFontSize, 7.5);
        } else if (estimatedTotalLines > 12) {
          // Very high line count
          titleFontSize = Math.min(titleFontSize, 8);
          snippetFontSize = Math.min(snippetFontSize, 8);
        } else if (estimatedTotalLines > 10) {
          // High line count
          titleFontSize = Math.min(titleFontSize, 8.5);
          snippetFontSize = Math.min(snippetFontSize, 8.5);
        }

        // Special handling for very long titles with no/minimal snippet
        if (estimatedTitleLines > 10 && !hasMeaningfulSnippet) {
          titleFontSize = Math.min(titleFontSize, 7.5); // Smaller for 10+ line titles
        } else if (estimatedTitleLines > 8 && !hasMeaningfulSnippet) {
          titleFontSize = Math.min(titleFontSize, 8);
        }

        let titleHeight = hasBothTitleAndSnippet ? 0.45 : boxH - 1.2; // Default heights, moved outside for scope

        // Special case: when title is null/empty, treat snippet as the main content
        const titleIsEmpty = !itemTitle || itemTitle.trim().length === 0;

        if (titleIsEmpty && itemSnippet) {
          // Treat snippet as the main content - render it as title
          const contentDisplay = itemSnippet.replace(/\n\n+/g, '\n');

          // Use title-like styling for the snippet when it's the only content
          slide.addText(contentDisplay, {
            x: x + 0.12,
            y: y + 0.75,
            w: boxW - 0.24,
            h: boxH - 1.2, // Use full content area
            fontSize: titleFontSize,
            bold: false,
            color: COLOR_FEED_TITLE,
            valign: 'top',
            align: 'left',
            wrap: true,
            shrinkText: true,
          });

          // Skip the rest - we've used the snippet as main content
        } else if (itemTitle) {
          // Clean up excessive newlines in title (replace multiple newlines with single)
          let titleDisplay = itemTitle.replace(/\n\n+/g, '\n'); // Replace double+ newlines with single

          // Split into actual lines to get real line count
          const actualTitleLines = titleDisplay.split('\n');
          const actualLineCount = actualTitleLines.length;

          // Apply additional font size reduction based on actual line count
          if (actualLineCount >= 10 && !hasMeaningfulSnippet) {
            // 10+ actual lines need smaller font
            titleFontSize = Math.min(titleFontSize, 7.5);
          } else if (actualLineCount >= 8 && !hasMeaningfulSnippet) {
            titleFontSize = Math.min(titleFontSize, 8);
          } else if (actualLineCount >= 6 && hasBothTitleAndSnippet) {
            // With snippet, 6+ lines of title needs adjustment
            titleFontSize = Math.min(titleFontSize, 8);
          } else if (actualLineCount >= 4 && hasBothTitleAndSnippet) {
            // 4+ lines of title with snippet needs smaller font
            titleFontSize = Math.min(titleFontSize, 8.5);
          }

          // Adjust title height based on actual line count when snippet exists
          if (hasBothTitleAndSnippet) {
            // Use the already declared snippetStartsWithEllipsis from above
            if (actualLineCount >= 5) {
              titleHeight = snippetStartsWithEllipsis ? 0.8 : 0.75; // Extra space when snippet continues
            } else if (actualLineCount >= 4) {
              titleHeight = snippetStartsWithEllipsis ? 0.7 : 0.65; // More space for continuation
            } else if (actualLineCount >= 3) {
              titleHeight = snippetStartsWithEllipsis ? 0.6 : 0.55; // More space for 3 line titles
            } else if (actualLineCount >= 2) {
              titleHeight = snippetStartsWithEllipsis ? 0.5 : 0.45; // Standard for 2 line titles
            } else {
              titleHeight = snippetStartsWithEllipsis ? 0.4 : 0.35; // Even single line needs more space
            }
          }

          // Apply line-based truncation first (priority over character-based)
          if (actualLineCount > 9 && !hasMeaningfulSnippet) {
            // Title-only: limit to 9 lines
            const truncatedLines = actualTitleLines.slice(0, 9);
            const lastLine = truncatedLines[8];
            if (lastLine.length > 62) {
              truncatedLines[8] = `${lastLine.substring(0, 62)}...`;
            } else {
              truncatedLines[8] = `${lastLine}...`;
            }
            titleDisplay = truncatedLines.join('\n');
          } else if (actualLineCount > 5 && hasBothTitleAndSnippet) {
            // Title with snippet: limit to 5 lines
            const truncatedLines = actualTitleLines.slice(0, 5);
            const lastLine = truncatedLines[4];
            if (lastLine.length > 62) {
              truncatedLines[4] = `${lastLine.substring(0, 62)}...`;
            } else {
              truncatedLines[4] = `${lastLine}...`;
            }
            titleDisplay = truncatedLines.join('\n');
          } else if (estimatedTitleLines > 10 && !hasMeaningfulSnippet) {
            // Title-only with excessive estimated lines - truncate to fit
            const maxChars = 650; // About 10 lines worth
            if (titleDisplay.length > maxChars) {
              titleDisplay = `${titleDisplay.substring(0, maxChars - 3)}...`;
            }
          } else if (estimatedTitleLines > 6 && hasBothTitleAndSnippet) {
            // Title with snippet, but title is too long - truncate more aggressively
            const maxChars = 380; // About 6 lines worth
            if (titleDisplay.length > maxChars) {
              titleDisplay = `${titleDisplay.substring(0, maxChars - 3)}...`;
            }
          }

          // If no meaningful snippet, give title all the content space
          if (!hasMeaningfulSnippet) {
            titleHeight = boxH - 1.2; // Use full content area
          }

          slide.addText(titleDisplay, {
            x: x + 0.12,
            y: y + 0.75,
            w: boxW - 0.24,
            h: titleHeight,
            fontSize: titleFontSize,
            bold: hasBothTitleAndSnippet, // Only bold when there's a real snippet
            color: COLOR_FEED_TITLE,
            valign: 'top',
            align: 'left',
            wrap: true,
            shrinkText: true, // Enable PPT's auto-shrink as fallback
          });
          // Only add snippet if it has meaningful content and we haven't already used it as main content
          if (hasMeaningfulSnippet) {
            // Clean up excessive newlines in snippet too
            let snippetDisplay = itemSnippet.replace(/\n\n+/g, '\n');

            // Check actual line count for snippet
            const actualSnippetLines = snippetDisplay.split('\n');
            const snippetLineCount = actualSnippetLines.length;

            // Apply line-based truncation for snippet
            if (snippetLineCount > 7) {
              // Limit snippet to 7 lines
              const truncatedLines = actualSnippetLines.slice(0, 7);
              const lastLine = truncatedLines[6];
              if (lastLine.length > 67) {
                truncatedLines[6] = `${lastLine.substring(0, 67)}...`;
              } else {
                truncatedLines[6] = `${lastLine}...`;
              }
              snippetDisplay = truncatedLines.join('\n');
            } else if (estimatedSnippetLines > 8) {
              // Fallback to character-based truncation for long wrapped text
              const maxChars = 550; // About 8 lines of snippet
              if (itemSnippet.length > maxChars) {
                snippetDisplay = `${itemSnippet.substring(0, maxChars - 3)}...`;
              }
            }

            // Calculate snippet Y position based on title height
            // Add extra gap if snippet starts with ellipsis (it's a continuation)
            const snippetStartsWithDots = snippetDisplay.trim().startsWith('...');
            const gapAfterTitle = snippetStartsWithDots ? 0.1 : 0.05; // Larger gap for continuations

            const snippetY = y + 0.75 + titleHeight + gapAfterTitle; // Title Y + title height + gap
            const snippetHeight = boxH - titleHeight - 1.0 - gapAfterTitle; // Remaining space after title and gap

            slide.addText(snippetDisplay, {
              x: x + 0.12,
              y: snippetY,
              w: boxW - 0.24,
              h: snippetHeight,
              fontSize: snippetFontSize,
              bold: false,
              color: COLOR_FEED_SNIPPET,
              valign: 'top',
              align: 'left',
              wrap: true,
              shrinkText: true, // Enable PPT's auto-shrink as fallback
            });
          }
        }
      } catch (error) {
        throw new Error(
          `Failed to add content text for item ${itemIndex} on slide ${slideNum}, position ${
            positionIndex + 1
          }. Title: "${(item.title || '').substring(0, 50)}...", Error: ${error.message}`,
        );
      }

      // Author handle (no hyperlink) - with error handling
      const authorHandle = sanitizeTextForPPT(`@${item.authorName || 'unknown'}`);
      try {
        slide.addText(authorHandle, {
          x: x + 0.12, // Further left alignment
          y: y + boxH - 0.45,
          w: 5,
          h: 0.3,
          fontSize: 10, // Slightly larger for better visibility
          bold: true,
          color: COLOR_FEED_PROFILE_NAME, // Dark blue-gray to match profile name
          valign: 'top',
          align: 'left',
        });
      } catch (error) {
        throw new Error(
          `Failed to add author handle "${authorHandle}" on slide ${slideNum}, position ${
            positionIndex + 1
          }. Error: ${error.message}`,
        );
      }

      // Footer metadata - with error handling and validation
      let metaText;
      try {
        const formattedDate = item.date ? pptFormatDate(new Date(item.date)) : 'Unknown date';
        const reach = item.reach || 0;
        const sentiment = sanitizeTextForPPT(item.sentiment || 'Neutral');
        const source = sanitizeTextForPPT(item.source || 'Unknown');
        metaText = `${formattedDate} Reach: ${reach}, Sentiment: ${sentiment}, Source: ${source}`;
      } catch (dateError) {
        const sentiment = sanitizeTextForPPT(item.sentiment || 'Neutral');
        const source = sanitizeTextForPPT(item.source || 'Unknown');
        metaText = `Date unavailable, Reach: ${
          item.reach || 0
        }, Sentiment: ${sentiment}, Source: ${source}`;
        // Date formatting error - use fallback text
      }

      try {
        slide.addText(metaText, {
          x: x + 0.12, // Further left alignment
          y: y + boxH - 0.25,
          w: boxW - 0.24,
          h: 0.3,
          fontSize: 8,
          bold: false,
          color: COLOR_FEED_META, // Medium gray
          valign: 'top',
          align: 'left',
        });
      } catch (error) {
        throw new Error(
          `Failed to add metadata text on slide ${slideNum}, position ${
            positionIndex + 1
          }. Text: "${metaText}", Error: ${error.message}`,
        );
      }

      // Add "View" button with arrow in top-right corner if URL is available
      if (item.contributionUri) {
        const buttonWidth = 0.8; // Increased for better fit
        const buttonHeight = 0.25;
        const buttonX = x + boxW - buttonWidth - 0.15;
        const buttonY = y + 0.15;
        const iconSize = 0.15;

        // Add button background with light gray fill and rounded corners
        try {
          slide.addShape(pptx.ShapeType.roundRect, {
            x: buttonX,
            y: buttonY,
            w: buttonWidth,
            h: buttonHeight,
            fill: { color: COLOR_FEED_VIEW_BG }, // Light gray background
            line: { color: COLOR_FEED_VIEW_BG, width: 0 }, // No border
            rectRadius: 0.03, // Slight rounding for button
            hyperlink: {
              url: sanitizeUrlForPPT(item.contributionUri),
              tooltip: `View on ${item.source}`,
            },
          });

          // Add the arrow icon on the LEFT side of the button if available
          if (linkIconBase64) {
            try {
              slide.addImage({
                data: linkIconBase64,
                x: buttonX + 0.08, // Left side of button
                y: buttonY + (buttonHeight - iconSize) / 2,
                w: iconSize,
                h: iconSize,
                hyperlink: {
                  url: sanitizeUrlForPPT(item.contributionUri),
                  tooltip: `View on ${item.source}`,
                },
              });
            } catch (error) {
              // Failed to add arrow icon - button will still work
            }
          }

          // Add "View" text to the right of the icon
          slide.addText('View', {
            x: buttonX + iconSize + 0.1, // Position after icon, tighter spacing
            y: buttonY,
            w: buttonWidth - iconSize - 0.15,
            h: buttonHeight,
            fontSize: 9,
            color: COLOR_FEED_VIEW_TEXT, // Dark gray text
            align: 'left',
            valign: 'middle',
            hyperlink: {
              url: sanitizeUrlForPPT(item.contributionUri),
              tooltip: `View on ${item.source}`,
            },
          });
        } catch (error) {
          // Failed to add view button - continue without it
        }
      }
    });
  }
};
